import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import * as yup from 'yup';

import {
	BookingOptionsDto,
	ErrorResponseDto,
	LegView,
	validateVoucherApi,
} from '@mopla/data-models';
import { getSubmitDisabled } from '@mopla/utils';

import { IValidVoucherData, useVoucherStore } from './useVoucherStore';

interface IUseRedeemVoucherFormProps {
	onValidateSuccess?(validatedVoucher: IValidVoucherData): void;
	leg: LegView;
	bookingOptions: BookingOptionsDto;
}

export interface IRedeemVoucherFormState {
	code: string;
}

export const RedeemVoucherValidationSchema = yup.object().shape({
	code: yup.string().required('required').max(23, 'max'),
});

export const useRedeemVoucherForm = (props: IUseRedeemVoucherFormProps) => {
	const { onValidateSuccess, leg, bookingOptions } = props;
	const { t } = useTranslation('validation');

	const validVoucher = useVoucherStore((state) => state.validVoucher);
	const voucherCode = useVoucherStore((state) => state.voucherCode);
	const validationError = useVoucherStore((state) => state.validationError);
	const setValidVoucher = useVoucherStore((state) => state.setValidVoucher);
	const setVoucherCode = useVoucherStore((state) => state.setVoucherCode);
	const setValidationError = useVoucherStore(
		(state) => state.setValidationError
	);
	const resetVoucherStore = useVoucherStore((state) => state.reset);

	const formCtx = useForm<IRedeemVoucherFormState>({
		/** the voucherCode can be provided from the store, e.g from the previous successful validation */
		defaultValues: { code: voucherCode || '' },
		resolver: yupResolver(RedeemVoucherValidationSchema),
		mode: 'onChange',
	});

	const {
		control,
		handleSubmit,
		reset,
		formState: { isSubmitting, isSubmitSuccessful },
	} = formCtx;

	/** Clear stale error from the store whenever the form mounts (e.g. user navigates back) */
	useEffect(() => {
		setValidationError(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const submitHandler: SubmitHandler<IRedeemVoucherFormState> = useCallback(
		async (formState) => {
			try {
				const { data: validatedVoucher } = await validateVoucherApi({
					body: { code: formState.code, leg, bookingOptions },
				});

				const validVoucherWithCode: IValidVoucherData = {
					...validatedVoucher,
					code: formState.code,
				};

				setValidVoucher(validVoucherWithCode);
				setVoucherCode(formState.code);
				setValidationError(null);

				reset({ code: formState.code });

				if (onValidateSuccess) {
					onValidateSuccess(validVoucherWithCode);
				}
			} catch (error: any) {
				const axiosError = error as AxiosError<ErrorResponseDto>;

				if (axiosError.status === 400 || axiosError.status === 404) {
					const errorMsg = t(
						`voucher.${axiosError.response?.data.code}`,
						t('voucher.not_eligible')
					);
					setValidationError(errorMsg);
					return;
				}
			}
		},
		[
			onValidateSuccess,
			reset,
			leg,
			setValidVoucher,
			setVoucherCode,
			setValidationError,
			t,
			bookingOptions,
		]
	);

	const resetForm = useCallback(() => {
		reset({ code: '' });
		resetVoucherStore();
	}, [reset, resetVoucherStore]);

	/** Clear a stale validation error as soon as the user edits the code */
	const clearError = useCallback(() => {
		if (validationError) {
			setValidationError(null);
		}
	}, [validationError, setValidationError]);

	return {
		onSubmit: handleSubmit(submitHandler),
		control,
		isSubmitting,
		isSubmitDisabled: getSubmitDisabled(formCtx, { expectDirtyField: 'code' }),
		isSubmitSuccessful,
		validationError,
		validVoucher,
		voucherCode,
		resetForm,
		clearError,
	};
};
